import SocketMessage from "app/types/SocketMessages";
import LocalStorageService from "./LocalStorage.service";
import { getRefreshToken } from "app/utilities/HttpClient";

export type SocketMessageHandler = (msg: SocketMessage) => void;

export default class WebSocketService {
  private socket: WebSocket | null = null;
  private url: string;
  private token: string;

  private messageListeners: SocketMessageHandler[] = [];

  private connectTimeout: ReturnType<typeof setTimeout> | null = null;

  // Keep-alive fields
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;

  // ⏳ Max Connection Duration field
  private maxDurationTimer: ReturnType<typeof setTimeout> | null = null;

  // Heartbeat Timings
  private readonly heartbeatInterval = 5 * 60 * 1000; // 5 minutes (to beat 10min idle timeout)
  private readonly pongTimeoutDelay = 3 * 60 * 1000; // 3 minutes (tolerance for background throttling)

  // Max Duration Timings (2 hours server limit)
  // Set to 1 hour 59 minutes (60,000ms less than 2 hours) for a graceful restart
  private readonly maxDuration = 2 * 60 * 60 * 1000 - 60000;

  private reconnecting = false;
  private reconnectDelay = 2000;
  private readonly maxReconnectDelay = 1 * 60 * 1000;

  private refreshTokenAttempts = 0;
  private readonly maxRefreshTokenAttempts = 5;
  private readonly refreshTokenRetryDelay = 1 * 60 * 1000;

  constructor(url: string, token: string) {
    this.url = url;
    this.token = token;

    // Network Status Handlers
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  }

  // ------------------------------
  // Public API
  // ------------------------------
  setToken(token: string) {
    this.token = token;
  }

  async connect() {
    if (!navigator.onLine) {
      this.log("warn", "Offline, cannot connect");
      return;
    }

    // 🔑 PRE-EMPTIVE AUTH CHECK: Refresh token if expired before connecting
    if (LocalStorageService.isAccessTokenExpired()) {
      this.log("log", "Token expired pre-connect. Initiating refresh...");
      this.refreshTokenAndReconnect();
      return;
    }

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      this.log("log", "Socket already open/connecting");
      return;
    }

    this.log("log", "Connecting...");
    this.socket = new WebSocket(`${this.url}?X-Auth-Token=${this.token}`);

    this.connectTimeout = setTimeout(() => {
      if (this.socket?.readyState === WebSocket.CONNECTING) {
        this.log("warn", "Connection timeout → closing");
        this.socket.close();
      }
    }, 10000);

    this.socket.onopen = this.handleOpen;
    this.socket.onmessage = this.handleMessage;
    this.socket.onerror = this.handleError;
    this.socket.onclose = this.handleClose;
  }

  send(message: string) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(message);
    } else {
      this.log("warn", "Cannot send, socket not open");
    }
  }

  onMessage(callback: SocketMessageHandler) {
    this.messageListeners.push(callback);
  }

  destroy() {
    this.log("log", "Destroying socket");

    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);

    this.stopPing();
    this.stopMaxDurationTimer(); // Ensure timer is stopped on destroy
    this.closeInternal();
    this.messageListeners = [];
  }

  // ------------------------------
  // Event Handlers
  // ------------------------------
  private handleOpen = () => {
    this.log("log", "Connected");
    if (this.connectTimeout) clearTimeout(this.connectTimeout);
    this.reconnectDelay = 2000;

    this.startPing();
    this.startMaxDurationTimer(); // 🆕 Start the max duration timer
  };

  private handleMessage = (event: MessageEvent) => {
    try {
      const parsed = JSON.parse(event.data) as SocketMessage;

      // PONG: Check for pong response and clear the timeout
      if (parsed.name === "pong") {
        if (this.pongTimeout) {
          clearTimeout(this.pongTimeout);
          this.pongTimeout = null;
          this.log("log", "WS Pong received. Heartbeat healthy.");
        }
        return;
      }

      this.messageListeners.forEach((cb) => cb(parsed));
    } catch (err) {
      this.log("warn", "Failed to parse socket message:", event.data);
    }
  };

  private handleError = (error: Event) => {
    this.log("error", "WebSocket error:", error);
    // Let handleClose do the heavy lifting, but we can attempt a refresh here
    this.refreshTokenAndReconnect();
  };

  private handleClose = (event: CloseEvent) => {
    this.log("warn", `Socket closed code=${event.code}`);

    this.stopPing();
    this.stopMaxDurationTimer(); // Ensure timer is stopped on close
    this.socket = null;
    if (this.connectTimeout) clearTimeout(this.connectTimeout);

    if (!navigator.onLine) {
      this.log("warn", "Offline → waiting for network");
      return;
    }

    // 🔑 POST-FAILURE AUTH CHECK: If token is expired after a close, refresh it.
    if (LocalStorageService.isAccessTokenExpired()) {
      this.log(
        "log",
        "Token expired on connection close. Attempting refresh..."
      );
      this.refreshTokenAndReconnect();
      return;
    }

    // Regular reconnect attempt
    this.scheduleReconnect();
  };

  private handleOnline = () => {
    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      this.log("log", "Online → reconnecting");
      this.connect();
    }
  };

  private handleOffline = () => {
    this.log("warn", "Offline → closing socket and stopping timers");
    this.closeInternal();
  };

  // ------------------------------
  // 🟢 WebSocket Ping Logic (Heartbeat)
  // ------------------------------
  private startPing() {
    this.stopPing();

    const pingData = JSON.stringify({ name: "ping" });

    this.pingTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(pingData);
        this.log("log", "WS Ping message sent. Starting pong timeout.");

        if (this.pongTimeout) clearTimeout(this.pongTimeout);
        // 🚨 PONG TIMEOUT: Initiate reconnect if the pong isn't received in time
        this.pongTimeout = setTimeout(() => {
          this.log(
            "error",
            "Pong timeout reached → connection is dead. Initiating reconnect."
          );
          this.closeInternal();
          this.scheduleReconnect();
        }, this.pongTimeoutDelay);
      } else {
        this.log("warn", "Socket not open, stopping Ping timer.");
        this.stopPing();
      }
    }, this.heartbeatInterval);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  // ------------------------------
  // ⏳ Max Duration Reset Logic
  // ------------------------------
  private startMaxDurationTimer() {
    this.stopMaxDurationTimer();

    this.maxDurationTimer = setTimeout(() => {
      this.log(
        "warn",
        "Max duration reached (2h server limit) → initiating graceful refresh."
      );

      // Check token before reconnecting to prevent immediate failure
      if (LocalStorageService.isAccessTokenExpired()) {
        this.log(
          "log",
          "Token expired on max duration reset. Initiating refresh..."
        );
        this.refreshTokenAndReconnect();
      } else {
        // If token is fine, close the socket to force a clean reconnect.
        // closeInternal() will stop this timer and handleClose will schedule the connect.
        this.closeInternal();
        this.scheduleReconnect(); // Force an immediate reconnect after the close
      }
    }, this.maxDuration);
  }

  private stopMaxDurationTimer() {
    if (this.maxDurationTimer) {
      clearTimeout(this.maxDurationTimer);
      this.maxDurationTimer = null;
    }
  }

  // ------------------------------
  // Reconnect & Token Refresh
  // ------------------------------
  private closeInternal() {
    this.stopPing();
    this.stopMaxDurationTimer(); // Ensure timer is stopped on internal close

    if (!this.socket) return;

    // Remove handlers to prevent recursive calls on socket.close()
    this.socket.onopen = null;
    this.socket.onclose = null;
    this.socket.onmessage = null;
    this.socket.onerror = null;

    this.socket.close();
    this.socket = null;

    if (this.connectTimeout) clearTimeout(this.connectTimeout);
  }

  private scheduleReconnect() {
    if (this.reconnecting) return;
    this.reconnecting = true;

    setTimeout(() => {
      this.reconnecting = false;
      this.log(
        "log",
        `Reconnect attempt (delay: ${this.reconnectDelay / 1000}s)`
      );
      this.connect();
      // Exponential backoff
      this.reconnectDelay = Math.min(
        this.reconnectDelay * 1.5,
        this.maxReconnectDelay
      );
    }, this.reconnectDelay);
  }

  private async refreshTokenAndReconnect() {
    this.closeInternal(); // Close the old connection immediately

    if (this.refreshTokenAttempts >= this.maxRefreshTokenAttempts) {
      this.log(
        "error",
        "Max refresh attempts reached. User needs to re-authenticate."
      );
      return;
    }

    this.refreshTokenAttempts++;
    this.log(
      "log",
      `Attempting token refresh (attempt ${this.refreshTokenAttempts})...`
    );

    try {
      const creds = await getRefreshToken();
      if (creds) {
        LocalStorageService.setAuthCredential(creds);
        this.token = creds.accessToken;

        this.log("log", "Token refreshed successfully → reconnecting");
        this.refreshTokenAttempts = 0; // Reset counter on success
        this.connect(); // Connect using the new token
        return;
      }
    } catch (err) {
      this.log("error", "Token refresh failed:", err);
    }

    // Schedule retry after delay
    setTimeout(
      () => this.refreshTokenAndReconnect(),
      this.refreshTokenRetryDelay
    );
  }

  private log(level: "log" | "warn" | "error", ...args: any[]) {
    const ts = new Date().toISOString().replace("T", " ").split(".")[0];
    console[level](`[WS ${ts}]`, ...args);
  }
}
